/**
 * Portal permissions are `<subject>.<action>` strings resolved per workspace
 * on the api (`api/src/lib/pmOnboarding/permissions.ts`) and delivered on
 * `BusinessPortalWorkspace.permissions`.
 *
 * The subjects listed here are the ones the web references by name, so a typo
 * in a nav gate or a policy section map fails the build. The role editor reads
 * the authoritative catalog (labels, grantability, hints) from the
 * `pmPermissionCatalog` query instead of hardcoding it.
 *
 * Everything here is presentation only. It decides what to show, never what to
 * allow — the resolvers re-derive the same grant from the database.
 */

export const PM_SUBJECTS = [
  'policies',
  'tdManaged',
  'leasingAgent',
  'properties',
  'pricing',
  'notices',
  'corporateScreenings',
  'compliance',
  'legal',
  'metrics',
  'agent',
  'training',
  'integration',
  'teamSettings',
  'roleAssignments',
] as const

export type PmSubject = (typeof PM_SUBJECTS)[number]

export type PmAction = 'read' | 'write'

export type PmPermission = `${PmSubject}.${PmAction}`

export interface PermissionChecker {
  /** True when `<subject>.<action>` is held. */
  can: (subject: PmSubject, action: PmAction) => boolean
  /** True when at least one subject grants the action. */
  canAny: (subjects: readonly PmSubject[], action: PmAction) => boolean
  /** True when every subject grants the action (vacuously true for []). */
  canAll: (subjects: readonly PmSubject[], action: PmAction) => boolean
  /** Raw check, for callers that already hold a permission string. */
  has: (permission: PmPermission) => boolean
}

/**
 * Wrap a workspace's permission list in the read accessors. Mirrors the
 * api-side `has` / `hasAny` / `hasAll` on `PmWorkspaceGrant` so the two sides
 * answer the same question the same way.
 */
export const buildPermissionChecker = (
  permissions: readonly string[] | null | undefined
): PermissionChecker => {
  const granted = new Set(permissions ?? [])
  const has = (permission: PmPermission) => granted.has(permission)
  const can = (subject: PmSubject, action: PmAction) =>
    has(`${subject}.${action}`)

  return {
    has,
    can,
    canAny: (subjects, action) => subjects.some((s) => can(s, action)),
    canAll: (subjects, action) => subjects.every((s) => can(s, action)),
  }
}
